import type { AdminClubDetail, AdminClubStaff, AdminUserRow } from '../../api/admin'

/**
 * Modelo de vista de /admin/users: club por usuario, filtros y orden.
 *
 * Todo corre en el cliente (mg, 2026-09-17: "solo front por ahora"). El
 * listado de `/admin/users` no trae el club, así que el índice se arma con
 * `/admin/clubs/{id}` (plantel) y `/admin/clubs/{id}/staff` (managers).
 */

export interface UserClubLink {
  club_id: number
  club_name: string
  kind: 'jugador' | 'staff'
}

export type ClubIndex = Map<number, UserClubLink[]>

export function buildClubIndex(
  clubs: AdminClubDetail[],
  staffByClub: Record<number, AdminClubStaff[]>,
): ClubIndex {
  const index: ClubIndex = new Map()
  const add = (userId: number, link: UserClubLink) => {
    const links = index.get(userId) ?? []
    if (!links.some((l) => l.club_id === link.club_id && l.kind === link.kind)) links.push(link)
    index.set(userId, links)
  }
  for (const c of clubs) {
    for (const p of c.players) add(p.user_id, { club_id: c.id, club_name: c.name, kind: 'jugador' })
    for (const s of staffByClub[c.id] ?? [])
      add(s.user_id, { club_id: c.id, club_name: c.name, kind: 'staff' })
  }
  return index
}

export type ClubFilter = 'all' | 'none' | number
export type RoleFilter = 'all' | 'admin' | 'jugador' | 'tester' | 'device_admin' | 'staff' | 'sin_rol'
export type AccessFilter = 'all' | 'demo' | 'sin_demo'
export type StatusFilter = 'all' | 'active' | 'inactive'

export interface UsersFilter {
  q: string
  club: ClubFilter
  role: RoleFilter
  access: AccessFilter
  status: StatusFilter
}

export const EMPTY_FILTER: UsersFilter = {
  q: '',
  club: 'all',
  role: 'all',
  access: 'all',
  status: 'all',
}

function matchesRole(u: AdminUserRow, links: UserClubLink[], role: RoleFilter): boolean {
  switch (role) {
    case 'all':
      return true
    case 'admin':
      return u.is_superuser
    case 'staff':
      return links.some((l) => l.kind === 'staff')
    case 'sin_rol':
      return !u.is_superuser && u.roles.length === 0 && !links.some((l) => l.kind === 'staff')
    default:
      return u.roles.includes(role)
  }
}

export function filterUsers(rows: AdminUserRow[], index: ClubIndex, f: UsersFilter): AdminUserRow[] {
  const q = f.q.trim().toLowerCase()
  return rows.filter((u) => {
    const links = index.get(u.id) ?? []
    if (q) {
      const hay = [u.username, u.email ?? '', u.display_name ?? '', ...links.map((l) => l.club_name)]
        .join(' ')
        .toLowerCase()
      if (!hay.includes(q)) return false
    }
    if (f.club === 'none' && links.length > 0) return false
    if (typeof f.club === 'number' && !links.some((l) => l.club_id === f.club)) return false
    if (!matchesRole(u, links, f.role)) return false
    if (f.access === 'demo' && !u.has_demo_access) return false
    if (f.access === 'sin_demo' && u.has_demo_access) return false
    if (f.status === 'active' && !u.is_active) return false
    if (f.status === 'inactive' && u.is_active) return false
    return true
  })
}

export type SortKey = 'username' | 'display' | 'club' | 'sessions' | 'trials' | 'last' | 'joined'
export type SortDir = 'asc' | 'desc'

/** Orden por defecto al clickear una columna: texto A→Z, números y fechas de mayor a menor. */
export const DEFAULT_DIR: Record<SortKey, SortDir> = {
  username: 'asc',
  display: 'asc',
  club: 'asc',
  sessions: 'desc',
  trials: 'desc',
  last: 'desc',
  joined: 'desc',
}

export function clubLabel(links: UserClubLink[]): string {
  return links.map((l) => l.club_name).join(', ')
}

function sortValue(u: AdminUserRow, index: ClubIndex, key: SortKey): string | number | null {
  switch (key) {
    case 'username':
      return u.username.toLowerCase()
    case 'display':
      return u.display_name ? u.display_name.toLowerCase() : null
    case 'club': {
      const label = clubLabel(index.get(u.id) ?? [])
      return label ? label.toLowerCase() : null
    }
    case 'sessions':
      return u.n_sessions
    case 'trials':
      return u.n_trials
    case 'last':
      return u.last_session_at ? Date.parse(u.last_session_at) : null
    case 'joined':
      return u.date_joined ? Date.parse(u.date_joined) : null
  }
}

/** Los vacíos (sin club, sin sesión, sin display) van siempre al final, en ambas direcciones. */
export function sortUsers(
  rows: AdminUserRow[],
  index: ClubIndex,
  key: SortKey,
  dir: SortDir,
): AdminUserRow[] {
  const sign = dir === 'asc' ? 1 : -1
  return [...rows].sort((a, b) => {
    const va = sortValue(a, index, key)
    const vb = sortValue(b, index, key)
    if (va === null && vb === null) return b.id - a.id
    if (va === null) return 1
    if (vb === null) return -1
    const cmp =
      typeof va === 'number' && typeof vb === 'number'
        ? va - vb
        : String(va).localeCompare(String(vb), 'es')
    return cmp !== 0 ? sign * cmp : b.id - a.id
  })
}
